﻿import { useMemo } from 'react'
import { orderBy, uniq } from 'lodash-es'
import { useGetEmployeeDataAccessClaimQuery } from '@/features/user/user.api.ts'
import { EmploymentEntity, EmploymentEntityCountryMap } from '@/common/types/employment.def.ts'
import { Department } from '@/common/types/work-info.def.ts'
import { SelectGroupOption, SelectOption } from '@/common/types/components.def.ts'
import { stringArrayToSelectOptions } from '@/utils/misc.ts'

/**
 * Client-side mirror of the server's entity-scoped EmployeeDataAccessClaim.
 *
 * The claim holds, per employment entity, TWO section sets:
 *   - general: consulted when the TARGET employee's department is NOT Human Capital
 *   - hc:      consulted when the TARGET employee's department IS Human Capital
 *
 * This is for UI affordances only - which entities to offer in a picker, which fields to disable.
 * The server re-checks every request, so a mismatch here degrades UX, never security.
 */
export const useEmployeeDataAccessClaim = () => {
  const { data: claim, isLoading } = useGetEmployeeDataAccessClaimQuery({})

  return useMemo(() => {
    const entityAccessDict = claim?.entityAccessDict ?? {}
    const accessibleEntities = Object.keys(entityAccessDict)

    /** Any grant for the entity at all, irrespective of section. */
    const hasEntityAccess = (entity?: string | null) =>
      !!entity && Object.prototype.hasOwnProperty.call(entityAccessDict, entity)

    /** Mirrors the server's ValidateAccess(entity, department, section). */
    const canAccessSection = (entity?: string | null, department?: string | null, section?: string | null) => {
      if (!entity || !department || !section) return false
      const access = entityAccessDict[entity]
      if (!access) return false
      return department === Department.HumanCapital
        ? access.hc.includes(section)
        : access.general.includes(section)
    }

    /** Department-not-care: granted in either set. Use for records that are a section in their own right. */
    const hasSectionAccess = (entity?: string | null, section?: string | null) => {
      if (!entity || !section) return false
      const access = entityAccessDict[entity]
      if (!access) return false
      return access.general.includes(section) || access.hc.includes(section)
    }

    /** A country is reachable when any entity within it is. */
    const hasCountryAccess = (country?: string | null) =>
      !!country && (EmploymentEntity[country as keyof typeof EmploymentEntity] ?? []).some(hasEntityAccess)

    /**
     * Mirrors the server's ValidateAccess(entity, section), which reads the GENERAL set only.
     * This is what download/export section validation uses: HC employees' data is filtered at
     * output time rather than by restricting which sections may be requested. Use this - not
     * hasSectionAccess - when deciding what to offer on an export form, so the UI cannot offer a
     * combination the server will then reject.
     */
    const validateSectionAccess = (entity?: string | null, section?: string | null) => {
      if (!entity || !section) return false
      return entityAccessDict[entity]?.general.includes(section) ?? false
    }

    /** Countries reachable at all, derived from the claimed entities. */
    const accessibleCountries = orderBy(Object.keys(EmploymentEntity).filter(hasCountryAccess), x => x, 'asc')

    /** Just get all sections from all entities */
    const accessibleSections = uniq(Object.values(claim?.entityAccessDict || {}).map(c => [...c.general, ...c.hc]).flat())

    /**
     * Entities whose GENERAL set grants EVERY one of the given sections. Matches the server, which
     * requires each requested section to be granted for each requested entity.
     */
    const entitiesWithAllSectionAccess = (sections: string[]) =>
      sections.length === 0
        ? accessibleEntities
        : accessibleEntities.filter(entity => sections.every(section => validateSectionAccess(entity, section)))

    const getEntitiesWithCountryAndAllSectionAccess = (countries: string[], sections: string[]): string[] => {
      const entities = entitiesWithAllSectionAccess(sections)
      const targetCountries = accessibleCountries.filter(x => countries.includes(x))
      return entities.filter(entity => EmploymentEntityCountryMap[entity] && targetCountries.includes(
        EmploymentEntityCountryMap[entity]))
    }

    const getCountriesWithAllSectionAccess = (sections: string[]): string[] => {
      const entities = entitiesWithAllSectionAccess(sections)
      return uniq(entities.map(entity => {
        const country = EmploymentEntityCountryMap[entity]
        if (country && accessibleCountries.includes(country)) return country
        return undefined
      }).filter(x => !!x)) as string[]
    }

    /** Grouped by country, matching EmploymentEntityOptions, but limited to what the user holds. */
    const accessibleEntityOptions: SelectGroupOption[] = Object.entries(EmploymentEntity)
    .map(([country, entities]) => ({
      // `label` is typed ReactNode, so it is not safe to use as a React key or to compare against.
      // `key` carries the country as a plain string for both purposes.
      key: country,
      label: country,
      options: orderBy(
        (entities as string[]).filter(hasEntityAccess).map(entity => ({ label: entity, value: entity })),
        x => x.label,
        'asc',
      ),
    }))
    .filter(group => group.options.length > 0)

    /**
     * Flat variant for controls that cannot render option groups (antd Checkbox.Group). Ordered by
     * country first so entities of the same country stay adjacent.
     */
    const accessibleEntityFlatOptions: SelectOption[] = accessibleEntityOptions
    .flatMap(group => group.options)

    return {
      isLoading,
      accessibleSections,
      accessibleCountries,
      accessibleCountryOptions: stringArrayToSelectOptions(accessibleCountries),
      accessibleEntities,
      accessibleEntityOptions,
      accessibleEntityFlatOptions,
      hasEntityAccess,
      hasSectionAccess,
      validateSectionAccess,
      entitiesWithAllSectionAccess,
      getEntitiesWithCountryAndAllSectionAccess,
      getCountriesWithAllSectionAccess,
      canAccessSection,
      hasCountryAccess,
    }
  }, [claim, isLoading])
}
